<script setup lang="ts">
/**
 * Document list for the incorporation E-Filing review — shows uploaded files
 * and/or generated documents, each opening in a new tab (download endpoint).
 */
import type { EfUploadedFile, EfGeneratedDoc } from '../composables/useIncorporationEfiling';

defineProps<{
  title?: string;
  uploaded?: EfUploadedFile[];
  generated?: EfGeneratedDoc[];
  emptyText?: string;
}>();

function sizeLabel(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const FILE_TYPE_LABEL: Record<string, string> = {
  PASSPORT: 'Passport / ID',
  ADDR_PROF: 'Proof of address',
  ALLOT_DOC: 'Allotment document',
  RC_DOC: 'Controller supporting doc',
};
</script>

<template>
  <div class="sg-ef-doclist">
    <div v-if="title" class="sg-ef-doclist__title">{{ title }}</div>

    <template v-if="(uploaded && uploaded.length) || (generated && generated.length)">
      <v-list density="compact" class="sg-ef-doclist__list">
        <v-list-item
          v-for="f in uploaded || []"
          :key="'u-' + f.fileCd"
          :href="f.url"
          target="_blank"
          rel="noopener"
          class="sg-ef-doclist__item"
        >
          <template #prepend>
            <v-icon size="18" color="primary">mdi-file-document-outline</v-icon>
          </template>
          <v-list-item-title class="sg-ef-doclist__name">{{ f.fileName || f.fileCd }}</v-list-item-title>
          <v-list-item-subtitle>
            {{ FILE_TYPE_LABEL[f.fileType] || f.fileType }}
            <span v-if="sizeLabel(f.fileSize)"> · {{ sizeLabel(f.fileSize) }}</span>
          </v-list-item-subtitle>
          <template #append>
            <v-icon size="16" color="grey">mdi-open-in-new</v-icon>
          </template>
        </v-list-item>

        <v-list-item
          v-for="d in generated || []"
          :key="'g-' + d.saveId"
          :href="d.url"
          target="_blank"
          rel="noopener"
          class="sg-ef-doclist__item"
        >
          <template #prepend>
            <v-icon size="18" color="teal">mdi-file-word-outline</v-icon>
          </template>
          <v-list-item-title class="sg-ef-doclist__name">{{ d.fileName }}</v-list-item-title>
          <v-list-item-subtitle>
            Generated document<span v-if="sizeLabel(d.fileSize)"> · {{ sizeLabel(d.fileSize) }}</span>
          </v-list-item-subtitle>
          <template #append>
            <v-icon size="16" color="grey">mdi-open-in-new</v-icon>
          </template>
        </v-list-item>
      </v-list>
    </template>

    <div v-else class="sg-ef-doclist__empty">{{ emptyText || 'No documents.' }}</div>
  </div>
</template>

<style scoped>
.sg-ef-doclist__title {
  font-size: 13px;
  font-weight: 700;
  color: #1a2336;
  margin-bottom: 4px;
}
.sg-ef-doclist__list {
  background: transparent;
  padding: 0;
}
.sg-ef-doclist__item {
  border: 1px solid #e6e9f0;
  border-radius: 8px;
  margin-bottom: 6px;
}
.sg-ef-doclist__name {
  font-size: 13px;
  font-weight: 600;
}
.sg-ef-doclist__empty {
  font-size: 12.5px;
  color: #8a95a8;
  padding: 6px 0;
}
</style>
